import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AlertTriangle, ArrowLeft, FileDown, Loader2, Save, Send, ShieldAlert, Smartphone,
} from "lucide-react";
import { toast } from "@/components/ui/sonner";
import PolicyLink from "@/components/PolicyLink";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import SegmentsEditor from "@/components/journey/SegmentsEditor";
import AlternativesEditor from "@/components/journey/AlternativesEditor";
import EmergencyPlanEditor from "@/components/journey/EmergencyPlanEditor";
import RiskAssessmentPanel from "@/components/journey/RiskAssessmentPanel";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PLAN_STATUS_LABELS, PLAN_STATUS_STYLES, ROUTE_CHANGE_REASONS } from "@/config/journeyCatalog";

const EDITABLE = ["draft", "returned_for_amendment"];

export default function JourneyPlanPage() {
  const { planId } = useParams();
  const { user, hasPermission } = useAuth();
  const [plan, setPlan] = useState(null);
  const [denied, setDenied] = useState(false);
  const [directions, setDirections] = useState({ outbound: { segments: [] }, return: { segments: [] } });
  const [alternatives, setAlternatives] = useState([]);
  const [emergencyPlan, setEmergencyPlan] = useState({});
  const [contactsNote, setContactsNote] = useState("");
  const [effectiveDate, setEffectiveDate] = useState("");
  const [reviewDate, setReviewDate] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changeDialog, setChangeDialog] = useState(false);
  const [changeForm, setChangeForm] = useState({ reason: "", details: "" });
  const [reviewDecision, setReviewDecision] = useState("approve");
  const [reviewComments, setReviewComments] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/journey-plans/${planId}`);
      setPlan(data);
      setDirections(data.directions || { outbound: { segments: [] }, return: { segments: [] } });
      setAlternatives(data.alternative_routes || []);
      setEmergencyPlan(data.emergency_plan || {});
      setContactsNote(data.emergency_contacts_note || "");
      setEffectiveDate(data.effective_date || "");
      setReviewDate(data.review_date || "");
      setDirty(false);
    } catch (err) {
      if (err?.response?.status === 404 || err?.response?.status === 403) setDenied(true);
      else toast.error(formatApiError(err));
    }
  }, [planId]);

  useEffect(() => {
    load();
  }, [load]);

  if (denied) {
    return (
      <div data-testid="plan-access-denied" className="flex flex-col items-center justify-center py-20 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-50 mb-6">
          <ShieldAlert className="h-8 w-8 text-red-600" />
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2">Access denied</h1>
        <p className="text-base text-slate-600 max-w-md mb-8">This route plan does not exist, or you are not authorised to view it. This attempt has been recorded.</p>
        <Link to="/children" className="inline-flex h-11 items-center rounded-md bg-primary px-6 text-sm font-semibold text-white hover:bg-brand-hover">Back to children</Link>
      </div>
    );
  }

  if (!plan) {
    return <div className="flex items-center gap-2 text-slate-600" data-testid="plan-loading"><Loader2 className="h-5 w-5 animate-spin" /> Loading route plan…</div>;
  }

  const editable = EDITABLE.includes(plan.status) && hasPermission("journeys:manage");
  const isAlternative = plan.plan_type === "alternative";
  const canReview = hasPermission("journeys:review") && plan.status === "awaiting_review"
    && (plan.created_by !== user?.id || user?.role === "super_admin");

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/journey-plans/${planId}`, {
        directions, alternative_routes: alternatives, emergency_plan: emergencyPlan,
        emergency_contacts_note: contactsNote || null,
        effective_date: effectiveDate || null, review_date: reviewDate || null,
      });
      setDirty(false);
      toast.success("Route plan saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    try {
      await save();
      const { data } = await api.post(`/journey-plans/${planId}/submit`);
      toast.success(data.message);
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.missing) {
        toast.error(`${detail.message} ${detail.missing.join("; ")}`);
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  const review = async () => {
    try {
      await api.post(`/journey-plans/${planId}/review`, { decision: reviewDecision, comments: reviewComments || null });
      toast.success("Review recorded");
      setReviewComments("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const newVersion = async () => {
    const reason = window.prompt("Reason for new version (required, recorded in audit history):");
    if (!reason?.trim()) return;
    try {
      const { data } = await api.post(`/journey-plans/${planId}/new-version`, { reason });
      toast.success(`Version ${data.version} created`);
      window.location.href = `/journey-plans/${data.id}`;
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reportChange = async () => {
    try {
      await api.post(`/journey-plans/${planId}/report-change`, changeForm);
      toast.success("Route change reported — plan and risk assessment flagged for review");
      setChangeDialog(false);
      setChangeForm({ reason: "", details: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const setSegments = (dir) => async (segs) => {
    // Persist immediately — the segment dialog's "Save segment" must store to the server,
    // otherwise the route never appears in My Journey to School / Plan PDF.
    const next = { ...directions, [dir]: { segments: segs } };
    setDirections(next);
    try {
      await api.patch(`/journey-plans/${planId}`, { directions: next });
      toast.success(`${dir === "outbound" ? "Home → School" : "School → Home"} route saved`);
    } catch (err) {
      toast.error(formatApiError(err));
      load();
    }
  };

  return (
    <div data-testid="journey-plan-page" className="space-y-5 max-w-5xl mx-auto">
      <Link to={`/children/${plan.child_id}`} className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-primary" data-testid="back-to-child">
        <ArrowLeft className="h-4 w-4" /> Back to {plan.child?.child_name}
      </Link>
      <div className="rounded-lg border border-border bg-white p-4 sm:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900" data-testid="plan-number">
                {plan.plan_number} <span className="text-base font-semibold text-slate-500">v{plan.version}</span>
              </h1>
              <PolicyLink module="journeys" />
              <Badge className={PLAN_STATUS_STYLES[plan.status]} data-testid="plan-status">{PLAN_STATUS_LABELS[plan.status]}</Badge>
              {isAlternative && (
                <Badge className="bg-amber-50 text-amber-700 border-transparent" data-testid="plan-type-alternative">Alternative / emergency route plan</Badge>
              )}
              {plan.review_required && (
                <Badge className="bg-red-50 text-red-700 border-transparent" data-testid="plan-review-required">
                  <AlertTriangle className="h-3 w-3 mr-1" /> Review required (route change)
                </Badge>
              )}
            </div>
            <p className="text-sm text-slate-600 mt-1" data-testid="plan-child-brief">
              {plan.child?.child_name} · {plan.child?.case_id} · {plan.child?.school_name || "No school"} · {plan.child?.council_name || "No local authority"}
            </p>
            <p className="text-xs text-slate-500 mt-0.5">
              Created by {plan.creator_name} · {new Date(plan.created_at).toLocaleString("en-GB")}
              {plan.submitted_at ? ` · Submitted ${new Date(plan.submitted_at).toLocaleString("en-GB")}` : ""}
              {plan.trainer_name ? ` · Trainer: ${plan.trainer_name}` : ""}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {editable && (
              <>
                <Button variant="outline" className="h-10" data-testid="plan-save-button" onClick={save} disabled={saving || !dirty}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save</>}
                </Button>
                <Button className="h-10 bg-ukgreen hover:bg-green-800 text-white font-semibold" data-testid="plan-submit-button" onClick={submit}>
                  <Send className="h-4 w-4 mr-2" /> Submit for review
                </Button>
              </>
            )}
            {["approved", "returned_for_amendment", "awaiting_review"].includes(plan.status) && hasPermission("journeys:manage") && (
              <Button variant="outline" className="h-10" data-testid="plan-new-version-button" onClick={newVersion}>New version</Button>
            )}
            <Button variant="outline" className="h-10" data-testid="plan-report-change-button" onClick={() => setChangeDialog(true)}>
              <AlertTriangle className="h-4 w-4 mr-2" /> Report route change
            </Button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/journey-plans/${planId}/pdf`} target="_blank" rel="noreferrer"
              data-testid="plan-pdf-button"
              className="inline-flex h-10 items-center rounded-md border border-border px-3 text-sm font-medium text-primary hover:bg-muted">
              <FileDown className="h-4 w-4 mr-2" /> Plan PDF
            </a>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/journey-plans/${planId}/visual-guide?direction=outbound`} target="_blank" rel="noreferrer"
              data-testid="visual-guide-outbound"
              className="inline-flex h-10 items-center rounded-md border border-border px-3 text-sm font-medium text-ukgreen hover:bg-ukgreen-light">
              My Journey to School
            </a>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/journey-plans/${planId}/visual-guide?direction=return`} target="_blank" rel="noreferrer"
              data-testid="visual-guide-return"
              className="inline-flex h-10 items-center rounded-md border border-border px-3 text-sm font-medium text-ukgreen hover:bg-ukgreen-light">
              My Journey Home
            </a>
            <Link to={`/journey-plans/${planId}/trainer`} data-testid="plan-trainer-view-link"
              className="inline-flex h-10 items-center rounded-md bg-brand-navy px-3 text-sm font-medium text-white hover:bg-slate-800">
              <Smartphone className="h-4 w-4 mr-2" /> Trainer view
            </Link>
          </div>
        </div>

        {plan.manager_review && (
          <div className="mt-2 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm" data-testid="plan-manager-review">
            <span className="font-semibold">Manager decision: {plan.manager_review.decision.replace(/_/g, " ")}</span>
            <span className="text-slate-600"> by {plan.manager_review.manager_name} · {(plan.manager_review.decided_at || "").slice(0, 16).replace("T", " ")}</span>
            {plan.manager_review.comments && <p className="text-xs text-slate-600 mt-1">{plan.manager_review.comments}</p>}
          </div>
        )}

        {plan.versions?.length > 1 && (
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="plan-version-history">
            {plan.versions.map((v) => (
              <Link key={v.id} to={`/journey-plans/${v.id}`} data-testid={`plan-version-${v.version}`}
                className={`rounded-full px-2.5 py-1 text-xs font-medium ${v.id === planId ? "bg-primary text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                v{v.version} · {PLAN_STATUS_LABELS[v.status]}
              </Link>
            ))}
          </div>
        )}

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-lg">
          <div className="space-y-1">
            <Label className="text-xs">Effective date</Label>
            <Input type="date" data-testid="plan-effective-date" value={effectiveDate} disabled={!editable}
              onChange={(e) => { setEffectiveDate(e.target.value); setDirty(true); }} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Review date</Label>
            <Input type="date" data-testid="plan-review-date" value={reviewDate} disabled={!editable}
              onChange={(e) => { setReviewDate(e.target.value); setDirty(true); }} />
          </div>
        </div>
      </div>

      {isAlternative && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="alternative-plan-banner">
          <span className="font-semibold">Alternative / emergency route plan.</span> This plan is only used in an emergency or when the primary route
          is blocked (e.g. bus cancelled, road or station closed). Like the primary route plan, it must be fully risk assessed and
          approved before it may be used with the child.
        </div>
      )}

      {canReview && (
        <div className="rounded-md border-2 border-indigo-300 bg-indigo-50 p-4 space-y-3" data-testid="plan-review-panel">
          <h3 className="font-heading font-bold text-slate-900">Manager review — route plan</h3>
          <p className="text-xs text-slate-600">Review the route, landmarks, crossings, alternatives, emergency plan and linked risk assessment before approving.</p>
          <div className="space-y-1.5">
            <Label>Decision</Label>
            <Select value={reviewDecision} onValueChange={setReviewDecision}>
              <SelectTrigger data-testid="plan-review-decision" className="h-11 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="approve" data-testid="plan-review-approve">Approve</SelectItem>
                <SelectItem value="return_for_amendment" data-testid="plan-review-return">Return for amendment</SelectItem>
                <SelectItem value="request_information" data-testid="plan-review-info">Request additional information</SelectItem>
                <SelectItem value="require_reassessment" data-testid="plan-review-reassess">Require route reassessment</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Comments {reviewDecision !== "approve" && "*"}</Label>
            <Textarea rows={2} value={reviewComments} onChange={(e) => setReviewComments(e.target.value)} data-testid="plan-review-comments" />
          </div>
          <Button onClick={review} data-testid="plan-review-submit" className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
            Record decision
          </Button>
        </div>
      )}

      <Tabs defaultValue="outbound" className="w-full">
        <TabsList className="bg-white border border-border flex flex-wrap h-auto">
          <TabsTrigger value="outbound" data-testid="tab-outbound">Home → School</TabsTrigger>
          <TabsTrigger value="return" data-testid="tab-return">School → Home</TabsTrigger>
          {!isAlternative && <TabsTrigger value="alternatives" data-testid="tab-alternatives">Alternative Routes</TabsTrigger>}
          <TabsTrigger value="emergency" data-testid="tab-emergency">Emergency Plan</TabsTrigger>
          <TabsTrigger value="risk" data-testid="tab-risk">Risk Assessment</TabsTrigger>
        </TabsList>
        <TabsContent value="outbound" className="mt-4">
          <Card><CardContent className="p-4 sm:p-5">
            <SegmentsEditor directionKey="outbound" label="Home → School" segments={directions.outbound?.segments || []}
              onChange={setSegments("outbound")} childId={plan.child_id} readOnly={!editable} />
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="return" className="mt-4">
          <Card><CardContent className="p-4 sm:p-5">
            <p className="text-xs text-slate-500 mb-3">The return journey may use a completely different route, transport, stops and safety considerations.</p>
            <SegmentsEditor directionKey="return" label="School → Home" segments={directions.return?.segments || []}
              onChange={setSegments("return")} childId={plan.child_id} readOnly={!editable} />
          </CardContent></Card>
        </TabsContent>
        {!isAlternative && (
        <TabsContent value="alternatives" className="mt-4">
          <Card><CardContent className="p-4 sm:p-5">
            <AlternativesEditor items={alternatives} readOnly={!editable}
              onChange={(v) => { setAlternatives(v); setDirty(true); }} />
          </CardContent></Card>
        </TabsContent>
        )}
        <TabsContent value="emergency" className="mt-4">
          <Card><CardContent className="p-4 sm:p-5">
            <EmergencyPlanEditor value={emergencyPlan} contactsNote={contactsNote} readOnly={!editable}
              onChange={(v) => { setEmergencyPlan(v); setDirty(true); }}
              onContactsNote={(v) => { setContactsNote(v); setDirty(true); }} />
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="risk" className="mt-4">
          <RiskAssessmentPanel plan={plan} reloadPlan={load} />
        </TabsContent>
      </Tabs>

      <Dialog open={changeDialog} onOpenChange={setChangeDialog}>
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Report route change</DialogTitle>
            <DialogDescription>Flags the plan and its risk assessment for review. Approved records are never silently modified.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3" data-testid="route-change-form">
            <div className="space-y-1.5">
              <Label>Reason</Label>
              <Select value={changeForm.reason} onValueChange={(v) => setChangeForm({ ...changeForm, reason: v })}>
                <SelectTrigger data-testid="route-change-reason"><SelectValue placeholder="Select reason" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {ROUTE_CHANGE_REASONS.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`change-reason-${r.value}`}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Details</Label>
              <Textarea rows={3} data-testid="route-change-details" value={changeForm.details}
                onChange={(e) => setChangeForm({ ...changeForm, details: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={reportChange} disabled={!changeForm.reason} data-testid="route-change-submit"
              className="h-11 bg-primary hover:bg-brand-hover text-white font-semibold">
              Report change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
